import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { PlusCircle, Trash, PencilSimple, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiscountManagementPage = () => {
  const [activeTab, setActiveTab] = useState('discounts');
  const [discounts, setDiscounts] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    company: '', vendor: '', end_client: '', discount_pct: '',
    volume_discount: '', tenure_discount: '', tenure_6m_pct: '',
    tenure_12m_pct: '', tenure_24m_pct: '',
    discount_mode: 'percent', discount_amount_per_hr: '',
    ach_fee: '', portal_fee: '', msp_fee: '', notes: '',
  });

  useEffect(() => { fetchDiscounts(); fetchReviewQueue(); }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/discounts`, { withCredentials: true });
      setDiscounts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchReviewQueue = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/discounts/review-queue`, { withCredentials: true });
      setReviewQueue(res.data || []);
    } catch (e) { console.error(e); }
  };

  const resolveReview = async (row) => {
    // Pre-fill the edit dialog with the row's raw values for the SA to fix
    setForm({
      company: row.company || '',
      vendor: row.vendor || '',
      end_client: row.end_client || '',
      discount_pct: row.raw?.discount || '',
      volume_discount: row.raw?.volume || '',
      tenure_discount: row.raw?.tenure || '',
      tenure_6m_pct: '', tenure_12m_pct: '', tenure_24m_pct: '',
      discount_mode: 'percent', discount_amount_per_hr: '',
      ach_fee: row.raw?.ach_fee || '', portal_fee: row.raw?.portal_fee || '', msp_fee: row.raw?.msp_fee || '',
      notes: `Resolved from review queue: ${row.reason || ''}`,
    });
    setEditId(`review:${row.id}`);
    setShowModal(true);
  };

  const discardReview = async (rid) => {
    if (!window.confirm('Discard this review row? It will not be added to the discounts list.')) return;
    try {
      await axios.delete(`${API_URL}/api/discounts/review-queue/${rid}`, { withCredentials: true });
      toast.success('Discarded');
      fetchReviewQueue();
    } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      discount_pct: parseFloat(form.discount_pct) || 0,
      discount_amount_per_hr: parseFloat(form.discount_amount_per_hr) || 0,
      tenure_6m_pct: parseFloat(form.tenure_6m_pct) || 0,
      tenure_12m_pct: parseFloat(form.tenure_12m_pct) || 0,
      tenure_24m_pct: parseFloat(form.tenure_24m_pct) || 0,
      ach_fee: parseFloat(form.ach_fee) || 0,
      portal_fee: parseFloat(form.portal_fee) || 0,
      msp_fee: parseFloat(form.msp_fee) || 0,
    };
    try {
      if (editId && String(editId).startsWith('review:')) {
        const rid = editId.split(':')[1];
        await axios.post(`${API_URL}/api/discounts/review-queue/${rid}/resolve`, payload, { withCredentials: true });
        toast.success('Resolved from review queue');
        await fetchReviewQueue();
      } else if (editId) {
        await axios.put(`${API_URL}/api/admin/discounts/${editId}`, payload, { withCredentials: true });
        toast.success('Discount updated');
      } else {
        await axios.post(`${API_URL}/api/admin/discounts`, payload, { withCredentials: true });
        toast.success('Discount created');
      }
      setShowModal(false);
      fetchDiscounts();
    } catch (e) {
      toast.error('Failed to save');
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount record?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/discounts/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchDiscounts();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const openEdit = (d) => {
    setForm({
      company: d.company, vendor: d.vendor, end_client: d.end_client,
      discount_pct: d.discount_pct || '', volume_discount: d.volume_discount || '',
      tenure_discount: d.tenure_discount || '',
      tenure_6m_pct: d.tenure_6m_pct || '',
      tenure_12m_pct: d.tenure_12m_pct || '',
      tenure_24m_pct: d.tenure_24m_pct || '',
      discount_mode: d.discount_mode || 'percent',
      discount_amount_per_hr: d.discount_amount_per_hr || '',
      ach_fee: d.ach_fee || '',
      portal_fee: d.portal_fee || '', msp_fee: d.msp_fee || '', notes: d.notes || '',
    });
    setEditId(d.id);
    setShowModal(true);
  };

  const openNew = () => {
    setForm({ company: '', vendor: '', end_client: '', discount_pct: '', volume_discount: '', tenure_discount: '', tenure_6m_pct: '', tenure_12m_pct: '', tenure_24m_pct: '', discount_mode: 'percent', discount_amount_per_hr: '', ach_fee: '', portal_fee: '', msp_fee: '', notes: '' });
    setEditId(null);
    setShowModal(true);
  };

  const filtered = discounts.filter(d =>
    `${d.company} ${d.vendor} ${d.end_client}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="discount-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Discount Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage Company → Vendor → End Client discount mappings (incl. tenure 6m/12m/24m and revenue-tier volume discounts)</p>
        </div>
        <Button onClick={openNew} data-testid="add-discount-btn">
          <PlusCircle size={16} className="mr-2" /> Add Discount
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: 'discounts', label: 'Discounts', count: discounts.length },
          { key: 'review', label: 'Review Queue', count: reviewQueue.length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${activeTab === tab.key ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 text-[10px] rounded-full ${tab.key === 'review' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'review' && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Warning className="text-amber-600" size={18} />
              <span>Rows the importer couldn't auto-parse — fix and resolve</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reviewQueue.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="review-empty">No rows pending review.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-amber-800">Company</th>
                      <th className="text-left px-3 py-2 font-medium text-amber-800">Vendor</th>
                      <th className="text-left px-3 py-2 font-medium text-amber-800">End Client</th>
                      <th className="text-left px-3 py-2 font-medium text-amber-800">Raw values</th>
                      <th className="text-left px-3 py-2 font-medium text-amber-800">Reason</th>
                      <th className="text-right px-3 py-2 font-medium text-amber-800">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewQueue.map(row => (
                      <tr key={row.id} className="border-b border-amber-100" data-testid={`review-row-${row.id}`}>
                        <td className="px-3 py-2 font-medium">{row.company}</td>
                        <td className="px-3 py-2">{row.vendor}</td>
                        <td className="px-3 py-2">{row.end_client || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">{Object.entries(row.raw || {}).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join(' · ') || '—'}</td>
                        <td className="px-3 py-2 text-xs text-amber-700">{row.reason}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" onClick={() => resolveReview(row)} data-testid={`resolve-review-${row.id}`}>Fix & Save</Button>
                          <Button size="sm" variant="ghost" className="ml-1 text-red-500" onClick={() => discardReview(row.id)} data-testid={`discard-review-${row.id}`}>Discard</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'discounts' && (
        <>
      <Input placeholder="Search by company, vendor, or end client..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="search-discounts" />

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Organization</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Vendor</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">End Client</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Discount</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Tenure 6m/12m/24m</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Volume</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Fees</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{d.company}</td>
                    <td className="px-4 py-3 text-slate-700">{d.vendor}</td>
                    <td className="px-4 py-3 text-slate-700">{d.end_client || '—'}</td>
                    <td className="px-4 py-3 text-center font-medium text-amber-700">
                      {d.discount_mode === 'flat_per_hour' ? `$${d.discount_amount_per_hr}/hr` : `${d.discount_pct}%`}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 text-xs">
                      {[d.tenure_6m_pct, d.tenure_12m_pct, d.tenure_24m_pct].some(v => v > 0)
                        ? `${d.tenure_6m_pct || 0}% / ${d.tenure_12m_pct || 0}% / ${d.tenure_24m_pct || 0}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 text-xs">
                      {d.volume_tiers && d.volume_tiers.length > 0
                        ? d.volume_tiers.map(t => `$${(t.threshold_usd/1e6).toFixed(1)}M→${t.pct}%`).join(', ')
                        : (d.volume_discount_pct > 0 ? `${d.volume_discount_pct}%` : '—')}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 text-xs">
                      {(d.ach_fee || d.portal_fee || d.msp_fee || d.ach_fee_amount) ? `A:${d.ach_fee || d.ach_fee_amount || 0} P:${d.portal_fee || 0} M:${d.msp_fee || 0}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`edit-discount-${d.id}`}><PencilSimple size={16} /></Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(d.id)} data-testid={`delete-discount-${d.id}`}><Trash size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">No discount records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? 'Edit Discount' : 'Add Discount'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-4">
            <div className="space-y-2"><Label>Organization *</Label><Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} data-testid="input-discount-company" /></div>
            <div className="space-y-2"><Label>Vendor *</Label><Input value={form.vendor} onChange={(e) => setForm({...form, vendor: e.target.value})} data-testid="input-discount-vendor" /></div>
            <div className="space-y-2"><Label>End Client *</Label><Input value={form.end_client} onChange={(e) => setForm({...form, end_client: e.target.value})} data-testid="input-discount-end-client" /></div>
            <div className="space-y-2"><Label>Discount %</Label><Input type="number" step="0.01" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: e.target.value})} /></div>
            <div className="space-y-2"><Label>Tenure 6m %</Label><Input type="number" step="0.01" value={form.tenure_6m_pct} onChange={(e) => setForm({...form, tenure_6m_pct: e.target.value})} /></div>
            <div className="space-y-2"><Label>Tenure 12m %</Label><Input type="number" step="0.01" value={form.tenure_12m_pct} onChange={(e) => setForm({...form, tenure_12m_pct: e.target.value})} /></div>
            <div className="space-y-2"><Label>Tenure 24m %</Label><Input type="number" step="0.01" value={form.tenure_24m_pct} onChange={(e) => setForm({...form, tenure_24m_pct: e.target.value})} /></div>
            <div className="space-y-2"><Label>ACH Fee ($)</Label><Input type="number" value={form.ach_fee} onChange={(e) => setForm({...form, ach_fee: e.target.value})} /></div>
            <div className="space-y-2"><Label>Portal Fee ($)</Label><Input type="number" value={form.portal_fee} onChange={(e) => setForm({...form, portal_fee: e.target.value})} /></div>
            <div className="space-y-2"><Label>MSP Fee ($)</Label><Input type="number" value={form.msp_fee} onChange={(e) => setForm({...form, msp_fee: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Volume Discount</Label><Input value={form.volume_discount} onChange={(e) => setForm({...form, volume_discount: e.target.value})} placeholder="e.g. 2% on >$500K annual" /></div>
            <div className="space-y-2"><Label>Tenure Discount Notes</Label><Input value={form.tenure_discount} onChange={(e) => setForm({...form, tenure_discount: e.target.value})} placeholder="e.g. 2.5% after 12m" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSave}>{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountManagementPage;
